import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, 
  Copy, 
  Check, 
  Mail, 
  Phone, 
  Building2, 
  User,
  Calendar,
  MapPin,
  Euro,
  FileText,
  Globe,
  Briefcase,
  LogOut,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Copy Button Component
const CopyButton = ({ text, size = 'sm' }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    if (!text) return;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: 'Kopiert!',
        description: 'In die Zwischenablage kopiert.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Kopieren fehlgeschlagen.',
      });
    }
  };

  if (!text) return null;

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={handleCopy}
      className="h-8 px-2 text-gray-500 hover:text-gray-700"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-600" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </Button>
  );
};

// Field Row Component
const FieldRow = ({ label, value, icon: Icon, type, copyable = true }) => {
  if (value === null || value === undefined || value === '') return null;

  const displayValue = () => {
    if (type === 'boolean') {
      return value ? (
        <Badge className="bg-green-100 text-green-700">Ja</Badge>
      ) : (
        <Badge variant="secondary">Nein</Badge>
      );
    }
    if (type === 'date') {
      return new Date(value).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    if (type === 'email') {
      return (
        <a href={`mailto:${value}`} className="text-blue-600 hover:underline flex items-center gap-1">
          <Mail className="w-4 h-4" />
          {value}
        </a>
      );
    }
    if (type === 'phone') {
      return (
        <a href={`tel:${value}`} className="text-blue-600 hover:underline flex items-center gap-1">
          <Phone className="w-4 h-4" />
          {value}
        </a>
      );
    }
    if (type === 'currency') {
      return `${Number(value).toLocaleString('de-DE')} €`;
    }
    if (type === 'array') {
      if (!Array.isArray(value) || value.length === 0) return null;
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
          ))}
        </div>
      );
    }
    if (type === 'multiline') {
      return <pre className="whitespace-pre-wrap text-sm text-gray-700">{value}</pre>;
    }
    return String(value);
  };

  const rendered = displayValue();
  if (rendered === null) return null;

  return (
    <div className="flex items-start py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 w-48 flex-shrink-0">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-sm font-medium text-gray-500">{label}</span>
      </div>
      <div className="flex-1 text-sm text-gray-900">{rendered}</div>
      {copyable && typeof value === 'string' && value.length > 0 && (
        <CopyButton text={value} />
      )}
    </div>
  );
};

// Section Component
const Section = ({ title, children }) => {
  const hasContent = React.Children.toArray(children).some(child => child !== null);
  if (!hasContent) return null;

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pb-2 border-b-2 border-gray-200">
        {title}
      </h3>
      <div className="bg-white rounded-lg border border-gray-200 px-4">
        {children}
      </div>
    </div>
  );
};

// Field mappings for different form types
const getFieldConfig = (type) => {
  if (type === 'contact') {
    return {
      sections: [
        {
          title: 'Kontaktdaten',
          fields: [
            { key: 'name', label: 'Name', icon: User },
            { key: 'email', label: 'E-Mail', icon: Mail, type: 'email' },
            { key: 'phone', label: 'Telefon', icon: Phone, type: 'phone' },
          ]
        },
        {
          title: 'Nachricht',
          fields: [
            { key: 'subject', label: 'Betreff', icon: FileText },
            { key: 'message', label: 'Nachricht', type: 'multiline', copyable: false },
          ]
        },
        {
          title: 'Metadaten',
          fields: [
            { key: 'created_at', label: 'Eingegangen', icon: Calendar, type: 'date', copyable: false },
          ]
        }
      ]
    };
  }

  if (type === 'onboarding') {
    return {
      sections: [
        {
          title: 'Persönliche Daten',
          fields: [
            { key: 'full_name', label: 'Name', icon: User },
            { key: 'email', label: 'E-Mail', icon: Mail, type: 'email' },
            { key: 'phone', label: 'Telefon', icon: Phone, type: 'phone' },
            { key: 'address', label: 'Adresse', icon: MapPin },
            { key: 'date_of_birth', label: 'Geburtsdatum', icon: Calendar },
            { key: 'nationality', label: 'Nationalität', icon: Globe },
            { key: 'residence_germany', label: 'Wohnsitz in DE', type: 'boolean' },
          ]
        },
        {
          title: 'Unternehmensdaten',
          fields: [
            { key: 'company_name', label: 'Firmenname', icon: Building2 },
            { key: 'alternative_company_name', label: 'Alternativname' },
            { key: 'company_location', label: 'Standort', icon: MapPin },
            { key: 'business_purpose', label: 'Geschäftszweck', icon: Briefcase },
          ]
        },
        {
          title: 'Kapital & Struktur',
          fields: [
            { key: 'number_of_shareholders', label: 'Anzahl Gesellschafter' },
            { key: 'managing_directors', label: 'Geschäftsführer' },
            { key: 'capital_type', label: 'Kapitalart' },
            { key: 'capital_amount', label: 'Stammkapital', type: 'currency' },
            { key: 'ownership_structure', label: 'Beteiligungsstruktur' },
          ]
        },
        {
          title: 'Zeitplan & Services',
          fields: [
            { key: 'founding_timeline', label: 'Gründungszeitplan' },
            { key: 'existing_bank_contact', label: 'Bankverbindung vorhanden', type: 'boolean' },
            { key: 'assisted_account_opening', label: 'Kontoeröffnung gewünscht', type: 'boolean' },
            { key: 'needs_landing_page', label: 'Landing Page gewünscht', type: 'boolean' },
            { key: 'social_media', label: 'Social Media', type: 'array' },
            { key: 'ai_bot_goals', label: 'KI-Bot Ziele', type: 'array' },
          ]
        },
        {
          title: 'Metadaten',
          fields: [
            { key: 'created_at', label: 'Eingegangen', icon: Calendar, type: 'date', copyable: false },
            { key: 'legal_confirmation', label: 'Rechtl. bestätigt', type: 'boolean', copyable: false },
          ]
        }
      ]
    };
  }

  if (type === 'seller-onboarding') {
    return {
      sections: [
        {
          title: 'Anbieter',
          fields: [
            { key: 'seller_name', label: 'Name', icon: User },
            { key: 'seller_email', label: 'E-Mail', icon: Mail, type: 'email' },
            { key: 'seller_phone', label: 'Telefon', icon: Phone, type: 'phone' },
            { key: 'seller_country', label: 'Land', icon: Globe },
            { key: 'seller_role', label: 'Rolle', type: 'array' },
          ]
        },
        {
          title: 'GmbH-Daten',
          fields: [
            { key: 'company_name', label: 'Firmenname', icon: Building2 },
            { key: 'company_register_number', label: 'Handelsregister-Nr.' },
            { key: 'register_court', label: 'Registergericht' },
            { key: 'company_location', label: 'Standort', icon: MapPin },
            { key: 'founding_date', label: 'Gründungsdatum', icon: Calendar },
            { key: 'share_capital', label: 'Stammkapital', type: 'currency' },
            { key: 'company_status', label: 'Status', type: 'array' },
          ]
        },
        {
          title: 'Finanzen & Verbindlichkeiten',
          fields: [
            { key: 'has_revenue', label: 'Umsatz vorhanden', type: 'boolean' },
            { key: 'revenue_amount', label: 'Umsatzhöhe' },
            { key: 'has_contracts', label: 'Verträge vorhanden', type: 'boolean' },
            { key: 'has_employees', label: 'Mitarbeiter', type: 'boolean' },
            { key: 'employee_count', label: 'Anzahl Mitarbeiter' },
            { key: 'has_liabilities', label: 'Verbindlichkeiten', type: 'boolean' },
            { key: 'liabilities_amount', label: 'Höhe Verbindlichkeiten' },
            { key: 'has_tax_liabilities', label: 'Steuerverbindlichkeiten' },
            { key: 'financial_statement_available', label: 'Jahresabschluss verfügbar' },
            { key: 'has_legal_disputes', label: 'Rechtsstreitigkeiten', type: 'boolean' },
          ]
        },
        {
          title: 'Gesellschafter & Geschäftsführung',
          fields: [
            { key: 'number_of_shareholders', label: 'Anzahl Gesellschafter' },
            { key: 'ownership_distribution', label: 'Anteilsverteilung' },
            { key: 'has_managing_director', label: 'GF vorhanden', type: 'boolean' },
          ]
        },
        {
          title: 'Verkauf',
          fields: [
            { key: 'transfer_type', label: 'Übertragungsart' },
            { key: 'asking_price', label: 'Kaufpreis', icon: Euro, type: 'currency' },
            { key: 'negotiable', label: 'Verhandelbar' },
            { key: 'transfer_timeline', label: 'Übergabezeitraum' },
            { key: 'sale_reason', label: 'Verkaufsgrund' },
          ]
        },
        {
          title: 'Metadaten',
          fields: [
            { key: 'created_at', label: 'Eingegangen', icon: Calendar, type: 'date', copyable: false },
            { key: 'seller_confirmation', label: 'Bestätigt', type: 'boolean', copyable: false },
          ]
        }
      ]
    };
  }

  return { sections: [] };
};

const AdminDetail = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;

  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const config = useMemo(() => {
    if (type === 'onboarding') {
      return { title: 'Erfassungsformular (Gründung)', endpoint: '/api/admin/onboarding', color: 'blue' };
    }
    if (type === 'seller-onboarding') {
      return { title: 'Anfrageformular (Verkauf)', endpoint: '/api/admin/seller-onboarding', color: 'orange' };
    }
    if (type === 'contact') {
      return { title: 'Kontaktanfrage', endpoint: '/api/admin/contact', color: 'gray' };
    }
    return null;
  }, [type]);

  const fieldConfig = useMemo(() => getFieldConfig(type), [type]);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (!config) {
      navigate('/admin');
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_URL}${config.endpoint}`, { headers });
        const list = Array.isArray(res.data) ? res.data : [];
        const found = list.find((x) => String(x.id) === String(id));

        if (!found) {
          toast({
            variant: 'destructive',
            title: 'Nicht gefunden',
            description: 'Der Eintrag wurde nicht gefunden.',
          });
          navigate('/admin');
          return;
        }

        setItem(found);
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Fehler beim Laden',
          description: 'Bitte logge dich erneut ein.',
        });
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [config, id, navigate, toast, token]);

  const logout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleDelete = async () => {
    if (!window.confirm('Diesen Eintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}${config.endpoint}/${id}`, { headers });
      
      toast({
        title: 'Gelöscht',
        description: 'Der Eintrag wurde erfolgreich gelöscht.',
      });
      
      navigate('/admin');
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler beim Löschen',
        description: err.response?.data?.detail || 'Der Eintrag konnte nicht gelöscht werden.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon" className="h-10 w-10">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{config?.title || 'Details'}</h1>
                <Badge 
                  className={
                    config?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                    config?.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }
                >
                  {type}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 font-mono">ID: {id}</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="gap-2">
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {loading ? (
          <Card className="border-2 border-gray-200">
            <CardContent className="py-16 text-center text-gray-600">Lade…</CardContent>
          </Card>
        ) : (
          <div>
            {/* Quick Contact Actions */}
            {item && (
              <Card className="border-2 border-gray-200 mb-6">
                <CardContent className="py-4">
                  <div className="flex flex-wrap gap-3">
                    {(item.email || item.seller_email) && (
                      <a href={`mailto:${item.email || item.seller_email}`}>
                        <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                          <Mail className="w-4 h-4" />
                          E-Mail senden
                        </Button>
                      </a>
                    )}
                    {(item.phone || item.seller_phone) && (
                      <a href={`tel:${item.phone || item.seller_phone}`}>
                        <Button variant="outline" className="gap-2">
                          <Phone className="w-4 h-4" />
                          Anrufen
                        </Button>
                      </a>
                    )}
                    <CopyButton 
                      text={JSON.stringify(item, null, 2)} 
                    />
                    <span className="text-sm text-gray-500 self-center">Alle Daten kopieren</span>
                    
                    {/* Lösch-Button */}
                    <div className="ml-auto">
                      <Button 
                        variant="outline" 
                        className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={handleDelete}
                        disabled={deleting}
                      >
                        <Trash2 className="w-4 h-4" />
                        {deleting ? 'Löschen...' : 'Löschen'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Structured Data */}
            {fieldConfig.sections.map((section, idx) => (
              <Section key={idx} title={section.title}>
                {section.fields.map((field) => (
                  <FieldRow
                    key={field.key}
                    label={field.label}
                    value={item?.[field.key]}
                    icon={field.icon}
                    type={field.type}
                    copyable={field.copyable !== false}
                  />
                ))}
              </Section>
            ))}

            {/* Raw JSON (collapsible) */}
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-700 py-2">
                Rohdaten anzeigen (JSON)
              </summary>
              <Card className="border border-gray-200 mt-2">
                <CardContent className="p-4">
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 overflow-auto max-h-96">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDetail;
